import { useState } from 'react'

import { SectionHeading } from '@/components/ui'
import { useReconciliationEntitled } from '@/modules/account'
import { useDialogActions } from '@/stores'
import { useStatements } from '../../hooks'
import { ReconciliationWorkspace } from '../dialogs/ReconciliationWorkspace'
import { CycleTable } from './CycleTable'
import { CycleToolbar } from './CycleToolbar'
import { type CycleAccountRow, cycleAccountRows } from './cycleRows'
import { useCycleUrlSync } from './useCycleUrlSync'

// Insights view of every billing cycle, one row per account, searchable and sortable, with the ones
// needing a human on top by default. Derived from store data, so no generation pass. Every account
// can open a cycle beside its statement pages; only an entitled one is told whether it balances.
export function ReconciliationOverview() {
  const statements = useStatements()
  const isReconciliationEntitled = useReconciliationEntitled()
  const { openDialog } = useDialogActions()
  useCycleUrlSync()

  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)

  const allRows = cycleAccountRows(statements)
  // The tally strip narrows the table to one verdict. Search is the table's own, so it can mark what
  // matched in each cell.
  const listed =
    statusFilter == null
      ? allRows
      : allRows.filter((row) => row.statusLabel === statusFilter)

  const openCycle = (row: CycleAccountRow) =>
    openDialog(ReconciliationWorkspace.id, {
      statementId: row.statementId,
      segmentId: row.segmentId,
    })

  const emptyMessage =
    allRows.length === 0
      ? isReconciliationEntitled
        ? 'No statements to reconcile yet. Upload bank or credit card statements on the Files tab and reconciliation will appear here.'
        : 'No statements yet. Upload bank or credit card statements on the Files tab and their cycles will appear here.'
      : 'No cycles match this search.'

  return (
    <div className="space-y-3">
      {/* Mounted regardless of the list below: a websocket update that empties it must not tear the
          workspace dialog out from under an in-progress edit. */}
      <ReconciliationWorkspace />

      <SectionHeading
        title={isReconciliationEntitled ? 'Reconciliation' : 'Statement cycles'}
        description={
          isReconciliationEntitled
            ? "Whether each statement's printed balances tie out to its transactions, cycle by cycle."
            : "Each statement's billing cycles and the transactions we read from them."
        }
      />

      <CycleToolbar
        rows={allRows}
        query={query}
        onQueryChange={setQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        isReconciliationEntitled={isReconciliationEntitled}
      />

      <div data-tour="cycle-table">
        <CycleTable
          rows={listed}
          isReconciliationEntitled={isReconciliationEntitled}
          query={query}
          onOpen={openCycle}
          emptyMessage={emptyMessage}
        />
      </div>
    </div>
  )
}
